import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { CATEGORIES, TOOLS, SAMPLE_INPUTS, globalSearch, FAVORITES_IDS } from '../../utils/toolsConfig';
import * as dt from '../../utils/datetimeConverters';
import * as clr from '../../utils/colorConverters';
import * as net from '../../utils/networkUtils';
import * as code from '../../utils/codeUtils';
import * as txt from '../../utils/textConverters';
// import * as enc from '../../utils/encodingConverters';
import './Toolbox.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function copyToClipboard(text, setCopied) {
  navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
}

function OutputBlock({ label, value, error, placeholder = '点击「转换」查看结果...' }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="modal-output-block">
      <div className="modal-label-row">
        <span className="modal-label">{label}</span>
        <button className="copy-btn" onClick={() => copyToClipboard(value, setCopied)} disabled={!value}>{copied ? '已复制!' : '复制'}</button>
      </div>
      {error ? <div className="modal-error">{error}</div> : <pre className="modal-output">{value || placeholder}</pre>}
    </div>
  );
}

function Textarea({ label, value, onChange, placeholder, rows = 8 }) {
  const handleClear = useCallback(() => onChange(''), [onChange]);
  return (
    <div className="modal-input-block">
      <div className="modal-label-row">
        <span className="modal-label">{label}</span>
        {value && <button className="clear-btn" onClick={handleClear}>清空</button>}
      </div>
      <textarea className="modal-textarea" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} spellCheck={false} rows={rows} />
    </div>
  );
}

// ─── Generic Convert Modal ────────────────────────────────────────────────────

const ConvertModal = ({ tool }) => {
  const [input, setInput] = useState(tool.sampleInput || SAMPLE_INPUTS[tool.inputLang] || '');
  const [inputB, setInputB] = useState(tool.sampleInput || SAMPLE_INPUTS[tool.inputLang] || '');
  const [pathExpr, setPathExpr] = useState('$.name');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');

  const run = useCallback(() => {
    setError(''); setOutput('');
    try {
      let result;
      if (tool.type === 'dual') result = tool.fn(input, inputB);
      else if (tool.type === 'path') result = tool.fn(input, pathExpr);
      else if (tool.type === 'generate') result = tool.fn(input);
      else if (tool.type === 'async') {
        tool.asyncFn(input).then(r => setOutput(r ?? '')).catch(e => setError(e.message));
        return;
      }
      else result = tool.fn(input);
      if (typeof result === 'object') result = JSON.stringify(result, null, 2);
      setOutput(result ?? '');
    } catch (e) { setError(e.message); }
  }, [tool, input, inputB, pathExpr]);

  const isDual = tool.type === 'dual';
  const isGenerate = tool.type === 'generate';
  const isPath = tool.type === 'path';

  return (
    <div className="modal-body">
      <div className="modal-inputs">
        {!isGenerate && (
          <Textarea
            label={isDual ? `输入 A（${tool.inputLang}）` : `输入（${tool.inputLang || '文本'}）`}
            value={input} onChange={setInput}
            placeholder={`在此输入 ${tool.inputLang || '内容'}...`}
          />
        )}
        {isPath && (
          <div className="modal-path-row">
            <span className="modal-label">JSONPath 表达式</span>
            <input className="path-input" value={pathExpr} onChange={e => setPathExpr(e.target.value)} placeholder="$.address.city" />
          </div>
        )}
        {isDual && (
          <Textarea
            label={`输入 B（${tool.inputLang}）`}
            value={inputB} onChange={setInputB}
            placeholder={`在此输入第二个 ${tool.inputLang}...`}
          />
        )}
        {isGenerate && (
          <Textarea label="JSON Schema（可选）" value={input} onChange={setInput} placeholder="输入 JSON Schema，留空则生成默认示例..." />
        )}
      </div>
      <div className="modal-run-row">
        <button className="run-btn" onClick={run}>转换</button>
      </div>
      <OutputBlock label={`输出（${tool.outputLang || '结果'}）`} value={output} error={error} />
    </div>
  );
};

// ─── QR Code Modal ────────────────────────────────────────────────────────────

const QrModal = ({ tool }) => {
  const [input, setInput] = useState('');
  const [svg, setSvg] = useState('');
  const [error, setError] = useState('');

  const run = useCallback(() => {
    setError('');
    try { setSvg(tool.qrFn(input || 'Hello')); }
    catch (e) { setError(e.message); }
  }, [tool, input]);

  return (
    <div className="modal-body">
      <Textarea label={tool.inputLabel || '输入内容'} value={input} onChange={setInput} placeholder={`输入${tool.inputLabel || '内容'}...`} rows={3} />
      <div className="modal-run-row"><button className="run-btn" onClick={run}>生成二维码</button></div>
      {error && <div className="modal-error">{error}</div>}
      {svg && (
        <div className="qr-output">
          <div className="modal-label-row"><span className="modal-label">二维码预览</span><span className="modal-hint">（演示用 SVG，实际使用建议集成 qrcode 库）</span></div>
          <div className="qr-svg-wrap" dangerouslySetInnerHTML={{ __html: svg }} />
        </div>
      )}
    </div>
  );
};

// ─── Datetime Modal ───────────────────────────────────────────────────────────

const DatetimeNowModal = () => {
  const [info, setInfo] = useState(dt.getCurrentTimeInfo());
  useEffect(() => {
    const t = setInterval(() => setInfo(dt.getCurrentTimeInfo()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="modal-body">
      <div className="dt-now-grid">
        {[
          ['本地时间', info.local],
          ['ISO 8601', info.iso],
          ['Unix 时间戳（秒）', info.unix_s],
          ['Unix 时间戳（毫秒）', info.unix_ms],
          ['UTC 时间', info.utc],
          ['时区', info.timezone],
          ['星期', `星期${info.weekday}`],
        ].map(([k, v]) => (
          <div key={k} className="dt-now-row">
            <span className="dt-now-key">{k}</span>
            <span className="dt-now-val">{String(v)}</span>
          </div>
        ))}
      </div>
      <div className="modal-hint-center">实时刷新中...</div>
    </div>
  );
};

const TimestampModal = () => {
  const [input, setInput] = useState(String(Math.floor(Date.now() / 1000)));
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    try { setResult(dt.timestampConvert(input)); }
    catch (e) { setError(e.message); setResult(null); }
  };
  return (
    <div className="modal-body">
      <Textarea label="输入时间戳或日期字符串" value={input} onChange={setInput} placeholder="例如：1704067200 或 2024-01-01 00:00:00" rows={2} />
      <div className="modal-run-row"><button className="run-btn" onClick={run}>转换</button></div>
      {error && <div className="modal-error">{error}</div>}
      {result && (
        <div className="dt-result-grid">
          {Object.entries(result).map(([k, v]) => (
            <div key={k} className="dt-now-row">
              <span className="dt-now-key">{k}</span>
              <span className="dt-now-val">{String(v)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TimezoneModal = () => {
  const [dateStr, setDateStr] = useState(new Date().toISOString().slice(0, 16));
  const [from, setFrom] = useState('Asia/Shanghai');
  const [to, setTo] = useState('America/New_York');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    try { setResult(dt.timezoneConvert(dateStr, from, to)); }
    catch (e) { setError(e.message); }
  };
  return (
    <div className="modal-body">
      <div className="modal-inputs" style={{ flexDirection: 'column', gap: 12 }}>
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">日期时间</span></div>
          <input className="modal-text-input" type="datetime-local" value={dateStr} onChange={e => setDateStr(e.target.value)} />
        </div>
        <div className="modal-inputs">
          <div className="modal-input-block">
            <div className="modal-label-row"><span className="modal-label">源时区</span></div>
            <select className="modal-select" value={from} onChange={e => setFrom(e.target.value)}>
              {dt.TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <div className="modal-input-block">
            <div className="modal-label-row"><span className="modal-label">目标时区</span></div>
            <select className="modal-select" value={to} onChange={e => setTo(e.target.value)}>
              {dt.TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
        </div>
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>转换</button></div>
      <OutputBlock label="结果" value={result} error={error} />
    </div>
  );
};

const TimeDiffModal = () => {
  const [d1, setD1] = useState('2024-01-01');
  const [d2, setD2] = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    try { setResult(dt.timeDiff(d1, d2)); }
    catch (e) { setError(e.message); setResult(null); }
  };
  return (
    <div className="modal-body">
      <div className="modal-inputs">
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">开始时间</span></div>
          <input className="modal-text-input" type="date" value={d1} onChange={e => setD1(e.target.value)} />
        </div>
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">结束时间</span></div>
          <input className="modal-text-input" type="date" value={d2} onChange={e => setD2(e.target.value)} />
        </div>
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>计算</button></div>
      {error && <div className="modal-error">{error}</div>}
      {result && (
        <div className="dt-result-grid">
          {Object.entries(result).map(([k, v]) => (
            <div key={k} className="dt-now-row">
              <span className="dt-now-key">{k}</span>
              <span className="dt-now-val">{String(v)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StopwatchModal = () => {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [laps, setLaps] = useState([]);
  const startRef = useRef(null);
  const baseRef = useRef(0);
  useEffect(() => {
    if (!running) return;
    startRef.current = Date.now();
    const t = setInterval(() => setElapsed(baseRef.current + Date.now() - startRef.current), 50);
    return () => clearInterval(t);
  }, [running]);
  const fmt = (ms) => {
    const s = Math.floor(ms / 1000), min = Math.floor(s / 60), h = Math.floor(min / 60);
    return `${String(h).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}.${String(ms % 1000).padStart(3, '0').slice(0, 2)}`;
  };
  const toggle = () => { if (running) { baseRef.current = elapsed; } setRunning(r => !r); };
  const reset = () => { setRunning(false); setElapsed(0); baseRef.current = 0; setLaps([]); };
  const lap = () => { setLaps(l => [...l, elapsed]); };
  return (
    <div className="modal-body">
      <div className="stopwatch-display">{fmt(elapsed)}</div>
      <div className="stopwatch-btns">
        <button className={`sw-btn ${running ? 'sw-stop' : 'sw-start'}`} onClick={toggle}>{running ? '暂停' : '开始'}</button>
        <button className="sw-btn sw-lap" onClick={lap} disabled={!running}>计次</button>
        <button className="sw-btn sw-reset" onClick={reset}>重置</button>
      </div>
      {laps.length > 0 && (
        <div className="sw-laps">
          {laps.map((l, i) => <div key={i} className="sw-lap-row"><span>第 {i + 1} 圈</span><span>{fmt(l)}</span></div>)}
        </div>
      )}
    </div>
  );
};

const CountdownModal = () => {
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(5);
  const [seconds, setSeconds] = useState(0);
  const [remaining, setRemaining] = useState(null);
  const [running, setRunning] = useState(false);
  useEffect(() => {
    if (!running || remaining === null || remaining <= 0) return;
    const t = setInterval(() => setRemaining(r => { if (r <= 1) { setRunning(false); return 0; } return r - 1; }), 1000);
    return () => clearInterval(t);
  }, [running, remaining]);
  const total = hours * 3600 + minutes * 60 + seconds;
  const start = () => { setRemaining(total); setRunning(true); };
  const pause = () => setRunning(r => !r);
  const reset = () => { setRunning(false); setRemaining(null); };
  const cur = remaining ?? total;
  const h = Math.floor(cur / 3600), m = Math.floor((cur % 3600) / 60), s = cur % 60;
  const pct = total > 0 ? ((total - (remaining ?? total)) / total) * 100 : 0;
  return (
    <div className="modal-body">
      <div className="countdown-inputs">
        {[['小时', hours, setHours, 23], ['分钟', minutes, setMinutes, 59], ['秒', seconds, setSeconds, 59]].map(([label, val, set, max]) => (
          <div key={label} className="countdown-field">
            <span className="modal-label">{label}</span>
            <input type="number" min="0" max={max} value={val} onChange={e => set(Math.min(max, Math.max(0, +e.target.value)))} className="countdown-input" disabled={running || remaining !== null} />
          </div>
        ))}
      </div>
      <div className="countdown-display">{`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`}</div>
      <div className="countdown-progress-bg"><div className="countdown-progress-bar" style={{ width: `${pct}%` }} /></div>
      {remaining === 0 && <div className="countdown-done">⏰ 时间到！</div>}
      <div className="stopwatch-btns">
        {remaining === null ? <button className="sw-btn sw-start" onClick={start}>开始</button> : <button className={`sw-btn ${running ? 'sw-stop' : 'sw-start'}`} onClick={pause}>{running ? '暂停' : '继续'}</button>}
        <button className="sw-btn sw-reset" onClick={reset}>重置</button>
      </div>
    </div>
  );
};

const CronModal = () => {
  const [expr, setExpr] = useState('0 9 * * 1-5');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    try { setResult(dt.parseCron(expr)); }
    catch (e) { setError(e.message); }
  };
  const examples = ['* * * * *', '0 * * * *', '0 0 * * *', '0 9 * * 1-5', '0 0 1 * *', '0 0 1 1 *', '*/5 * * * *'];
  return (
    <div className="modal-body">
      <Textarea label="Cron 表达式（5字段: 分 时 日 月 周）" value={expr} onChange={setExpr} placeholder="0 9 * * 1-5" rows={2} />
      <div className="cron-examples">
        <span className="modal-label" style={{ marginRight: 8 }}>常用示例：</span>
        {examples.map(e => <button key={e} className="cron-ex-btn" onClick={() => setExpr(e)}>{e}</button>)}
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>解析</button></div>
      <OutputBlock label="解析结果" value={result} error={error} />
    </div>
  );
};

const RandomDateModal = () => {
  const [start, setStart] = useState('2024-01-01');
  const [end, setEnd] = useState('2024-12-31');
  const [count, setCount] = useState(10);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    try { setResult(dt.randomDateGenerate(start, end, count).join('\n')); }
    catch (e) { setError(e.message); }
  };
  return (
    <div className="modal-body">
      <div className="modal-inputs">
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">开始日期</span></div>
          <input className="modal-text-input" type="date" value={start} onChange={e => setStart(e.target.value)} />
        </div>
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">结束日期</span></div>
          <input className="modal-text-input" type="date" value={end} onChange={e => setEnd(e.target.value)} />
        </div>
        <div className="modal-input-block" style={{ maxWidth: 120 }}>
          <div className="modal-label-row"><span className="modal-label">生成数量</span></div>
          <input className="modal-text-input" type="number" min="1" max="50" value={count} onChange={e => setCount(+e.target.value)} />
        </div>
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>生成</button></div>
      <OutputBlock label="生成结果" value={result} error={error} />
    </div>
  );
};

const Hms2SModal = () => {
  const [input, setInput] = useState('01:30:45');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    try { setResult(String(dt.hmsToSeconds(input)) + ' 秒'); }
    catch (e) { setError(e.message); }
  };
  return (
    <div className="modal-body">
      <Textarea label="输入时:分:秒（HH:MM:SS）" value={input} onChange={setInput} placeholder="01:30:45" rows={2} />
      <div className="modal-run-row"><button className="run-btn" onClick={run}>转换</button></div>
      <OutputBlock label="秒数" value={result} error={error} />
    </div>
  );
};

const S2HmsModal = () => {
  const [input, setInput] = useState('5445');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    try {
      const r = dt.secondsToHms(input);
      setResult(`${r.formatted}\n${r.hours}小时 ${r.minutes}分 ${r.seconds}秒`);
    } catch (e) { setError(e.message); }
  };
  return (
    <div className="modal-body">
      <Textarea label="输入秒数" value={input} onChange={setInput} placeholder="5445" rows={2} />
      <div className="modal-run-row"><button className="run-btn" onClick={run}>转换</button></div>
      <OutputBlock label="时:分:秒" value={result} error={error} />
    </div>
  );
};

// ─── Color Modals ─────────────────────────────────────────────────────────────

const ColorModal = () => {
  const [input, setInput] = useState('#3b82f6');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    try { setResult(clr.convertColor(input)); }
    catch (e) { setError(e.message); setResult(null); }
  };
  return (
    <div className="modal-body">
      <div className="color-input-row">
        <input type="color" className="color-native-picker" value={input.startsWith('#') && input.length === 7 ? input : '#3b82f6'} onChange={e => setInput(e.target.value)} />
        <input className="modal-text-input color-text-input" value={input} onChange={e => setInput(e.target.value)} placeholder="#3b82f6 / rgb(59,130,246) / hsl(217,91%,60%)" />
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>转换</button></div>
      {error && <div className="modal-error">{error}</div>}
      {result && (
        <div className="color-results">
          <div className="color-preview-bar" style={{ background: result.hex }} />
          {[['HEX', result.hex], ['RGB', result.rgb], ['RGBA', result.rgba], ['HSL', result.hsl], ['HSV', result.hsv], ['CMYK', result.cmyk]].map(([label, val]) => (
            <div key={label} className="dt-now-row">
              <span className="dt-now-key">{label}</span>
              <span className="dt-now-val color-val-copyable" onClick={() => navigator.clipboard.writeText(val)} title="点击复制">{val}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ContrastModal = () => {
  const [c1, setC1] = useState('#ffffff');
  const [c2, setC2] = useState('#3b82f6');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    try { setResult(clr.contrastCheck(c1, c2)); }
    catch (e) { setError(e.message); setResult(null); }
  };
  const badge = (pass) => <span className={`wcag-badge ${pass ? 'pass' : 'fail'}`}>{pass ? '✓ 通过' : '✗ 未通过'}</span>;
  return (
    <div className="modal-body">
      <div className="modal-inputs">
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">前景色</span></div>
          <div className="color-input-row"><input type="color" className="color-native-picker" value={c1} onChange={e => setC1(e.target.value)} /><input className="modal-text-input" value={c1} onChange={e => setC1(e.target.value)} /></div>
        </div>
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">背景色</span></div>
          <div className="color-input-row"><input type="color" className="color-native-picker" value={c2} onChange={e => setC2(e.target.value)} /><input className="modal-text-input" value={c2} onChange={e => setC2(e.target.value)} /></div>
        </div>
      </div>
      <div className="contrast-preview" style={{ color: c1, background: c2 }}>示例文字 Sample Text 123</div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>检查</button></div>
      {error && <div className="modal-error">{error}</div>}
      {result && (
        <div className="dt-result-grid">
          <div className="dt-now-row"><span className="dt-now-key">对比度</span><span className="dt-now-val contrast-ratio">{result.ratioStr}</span></div>
          <div className="dt-now-row"><span className="dt-now-key">WCAG 评级</span><span className="dt-now-val">{result.wcag}</span></div>
          <div className="dt-now-row"><span className="dt-now-key">AA（普通文字 ≥4.5:1）</span><span className="dt-now-val">{badge(result.aa_normal)}</span></div>
          <div className="dt-now-row"><span className="dt-now-key">AA（大文字 ≥3:1）</span><span className="dt-now-val">{badge(result.aa_large)}</span></div>
          <div className="dt-now-row"><span className="dt-now-key">AAA（普通文字 ≥7:1）</span><span className="dt-now-val">{badge(result.aaa_normal)}</span></div>
          <div className="dt-now-row"><span className="dt-now-key">AAA（大文字 ≥4.5:1）</span><span className="dt-now-val">{badge(result.aaa_large)}</span></div>
        </div>
      )}
    </div>
  );
};

const PaletteModal = () => {
  const [color, setColor] = useState('#3b82f6');
  const [mode, setMode] = useState('shades');
  const [palette, setPalette] = useState([]);
  const modes = [['shades','明暗渐变'],['analogous','类似色'],['complementary','互补色'],['triadic','三角色'],['tetradic','四角色'],['monochromatic','单色系']];
  const run = () => {
    try { setPalette(clr.generatePalette(color, mode)); }
    catch (e) { setPalette([]); }
  };
  const [copied, setCopied] = useState('');
  return (
    <div className="modal-body">
      <div className="modal-inputs" style={{ alignItems: 'center' }}>
        <div className="modal-input-block" style={{ maxWidth: 200 }}>
          <div className="modal-label-row"><span className="modal-label">基础颜色</span></div>
          <div className="color-input-row">
            <input type="color" className="color-native-picker" value={color} onChange={e => setColor(e.target.value)} />
            <input className="modal-text-input" value={color} onChange={e => setColor(e.target.value)} />
          </div>
        </div>
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">配色模式</span></div>
          <select className="modal-select" value={mode} onChange={e => setMode(e.target.value)}>
            {modes.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>生成</button></div>
      {palette.length > 0 && (
        <div className="palette-grid">
          {palette.map((c) => (
            <div key={c} className="palette-swatch" style={{ background: c }} onClick={() => { navigator.clipboard.writeText(c); setCopied(c); setTimeout(() => setCopied(''), 1500); }} title={c}>
              <span className="palette-label">{copied === c ? '已复制' : c.toUpperCase()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ColorPickerModal = () => {
  const [color, setColor] = useState('#3b82f6');
  const [copied, setCopied] = useState('');
  const result = useMemo(() => { try { return clr.convertColor(color); } catch { return null; } }, [color]);
  return (
    <div className="modal-body">
      <div className="colorpick-main">
        <input type="color" className="colorpick-big" value={color} onChange={e => setColor(e.target.value)} />
        <div className="colorpick-info">
          <div className="colorpick-preview" style={{ background: color }} />
          {result && [['HEX', result.hex], ['RGB', result.rgb], ['HSL', result.hsl]].map(([k, v]) => (
            <div key={k} className="dt-now-row" style={{ cursor: 'pointer' }} onClick={() => { navigator.clipboard.writeText(v); setCopied(k); setTimeout(() => setCopied(''), 1500); }}>
              <span className="dt-now-key">{k}</span>
              <span className="dt-now-val">{copied === k ? '✓ 已复制' : v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Text Modals ──────────────────────────────────────────────────────────────

const CaseModal = () => {
  const [input, setInput] = useState('Hello World 你好世界');
  const modes = [
    ['upper','全大写'],['lower','全小写'],['title','首字母大写'],['sentence','句首大写'],
    ['camel','camelCase'],['pascal','PascalCase'],['snake','snake_case'],['kebab','kebab-case'],
    ['constant','CONSTANT_CASE'],['toggle','大小写反转'],['alternating','交替大小写'],
  ];
  const [selected, setSelected] = useState('upper');
  const [output, setOutput] = useState('');
  // const [copied, setCopied] = useState(false);
  const run = () => setOutput(txt.caseConvert(input, selected));
  return (
    <div className="modal-body">
      <Textarea label="输入文本" value={input} onChange={setInput} placeholder="输入文本..." rows={4} />
      <div className="case-modes">
        {modes.map(([v, l]) => (
          <button key={v} className={`case-btn ${selected === v ? 'active' : ''}`} onClick={() => { setSelected(v); setOutput(txt.caseConvert(input, v)); }}>{l}</button>
        ))}
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>转换</button></div>
      <OutputBlock label="输出" value={output} />
    </div>
  );
};

const SortModal = () => {
  const [input, setInput] = useState('香蕉\n苹果\n橙子\n草莓\n葡萄');
  const [order, setOrder] = useState('asc');
  const [byLen, setByLen] = useState(false);
  const [unique, setUnique] = useState(false);
  const [output, setOutput] = useState('');
  const run = () => setOutput(txt.sortText(input, { order, byLength: byLen, unique }));
  return (
    <div className="modal-body">
      <Textarea label="输入文本（每行一项）" value={input} onChange={setInput} rows={6} />
      <div className="sort-options">
        <label className="sort-option"><input type="radio" name="order" value="asc" checked={order === 'asc'} onChange={() => setOrder('asc')} />升序</label>
        <label className="sort-option"><input type="radio" name="order" value="desc" checked={order === 'desc'} onChange={() => setOrder('desc')} />降序</label>
        <label className="sort-option"><input type="checkbox" checked={byLen} onChange={e => setByLen(e.target.checked)} />按长度</label>
        <label className="sort-option"><input type="checkbox" checked={unique} onChange={e => setUnique(e.target.checked)} />去重</label>
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>排序</button></div>
      <OutputBlock label="排序结果" value={output} />
    </div>
  );
};

const DedupeModal = () => {
  const [input, setInput] = useState('苹果\n香蕉\n苹果\n橙子\n香蕉\n葡萄');
  const [cs, setCs] = useState(true);
  const [trim, setTrim] = useState(true);
  const [result, setResult] = useState(null);
  const run = () => setResult(txt.deduplicateText(input, { caseSensitive: cs, trimLines: trim }));
  return (
    <div className="modal-body">
      <Textarea label="输入文本（每行一项）" value={input} onChange={setInput} rows={6} />
      <div className="sort-options">
        <label className="sort-option"><input type="checkbox" checked={cs} onChange={e => setCs(e.target.checked)} />区分大小写</label>
        <label className="sort-option"><input type="checkbox" checked={trim} onChange={e => setTrim(e.target.checked)} />去除首尾空白</label>
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>去重</button></div>
      {result && <>
        <div className="dedupe-stat">移除了 <strong>{result.removedCount}</strong> 条重复项</div>
        <OutputBlock label="去重后的文本" value={result.unique} />
        {result.duplicates.length > 0 && <OutputBlock label="重复项列表" value={result.duplicates.join('\n')} />}
      </>}
    </div>
  );
};

const DiffModal = () => {
  const [a, setA] = useState('第一行\n第二行\n第三行');
  const [b, setB] = useState('第一行\n修改的第二行\n第三行\n新增行');
  const [output, setOutput] = useState('');
  const run = () => setOutput(txt.compareText(a, b));
  return (
    <div className="modal-body">
      <div className="modal-inputs">
        <Textarea label="文本 A" value={a} onChange={setA} rows={8} />
        <Textarea label="文本 B" value={b} onChange={setB} rows={8} />
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>比较</button></div>
      <OutputBlock label="差异结果" value={output} />
    </div>
  );
};

const AnalysisModal = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const run = () => setResult(txt.analyzeText(input));
  return (
    <div className="modal-body">
      <Textarea label="输入文本" value={input} onChange={setInput} placeholder="在此粘贴要分析的文本..." rows={8} />
      <div className="modal-run-row"><button className="run-btn" onClick={run}>分析</button></div>
      {result && (
        <div className="analysis-grid">
          <div className="analysis-stats">
            {[['总字符数', result.chars], ['非空字符数', result.charsNoSpace], ['单词数', result.words], ['句子数', result.sentences], ['行数', result.lines], ['段落数', result.paragraphs], ['平均词长', result.avgWordLength]].map(([k, v]) => (
              <div key={k} className="analysis-stat-item"><span className="analysis-stat-val">{v}</span><span className="analysis-stat-key">{k}</span></div>
            ))}
          </div>
          {result.topWords.length > 0 && (
            <div className="analysis-section">
              <div className="modal-label">高频词汇</div>
              <div className="analysis-freq">{result.topWords.join('  ')}</div>
            </div>
          )}
          {result.topChars.length > 0 && (
            <div className="analysis-section">
              <div className="modal-label">高频字符</div>
              <div className="analysis-freq">{result.topChars.join('  ')}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PasswordModal = () => {
  const [length, setLength] = useState(16);
  const [count, setCount] = useState(5);
  const [upper, setUpper] = useState(true);
  const [lower, setLower] = useState(true);
  const [nums, setNums] = useState(true);
  const [syms, setSyms] = useState(true);
  const [result, setResult] = useState([]);
  const run = () => setResult(txt.generatePassword(length, { upper, lower, numbers: nums, symbols: syms, count }));
  return (
    <div className="modal-body">
      <div className="pw-options">
        <div className="pw-row">
          <label className="modal-label">密码长度</label>
          <input type="range" min="4" max="128" value={length} onChange={e => setLength(+e.target.value)} className="pw-slider" />
          <span className="pw-len">{length}</span>
        </div>
        <div className="pw-row">
          <label className="modal-label">生成数量</label>
          <input type="number" min="1" max="20" value={count} onChange={e => setCount(Math.min(20, +e.target.value))} className="pw-count" />
        </div>
        <div className="pw-checks">
          {[['大写字母 A-Z', upper, setUpper], ['小写字母 a-z', lower, setLower], ['数字 0-9', nums, setNums], ['特殊符号 !@#...', syms, setSyms]].map(([l, v, s]) => (
            <label key={l} className="sort-option"><input type="checkbox" checked={v} onChange={e => s(e.target.checked)} />{l}</label>
          ))}
        </div>
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>生成密码</button></div>
      {result.length > 0 && (
        <div className="pw-results">
          {result.map((pw, i) => (
            <div key={i} className="pw-item">
              <code className="pw-text">{pw}</code>
              <button className="copy-btn" onClick={() => navigator.clipboard.writeText(pw)}>复制</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const LoremModal = () => {
  const [paras, setParas] = useState(3);
  const [output, setOutput] = useState('');
  const run = () => setOutput(txt.loremIpsum(paras));
  return (
    <div className="modal-body">
      <div className="modal-inputs" style={{ alignItems: 'center' }}>
        <div className="modal-input-block" style={{ maxWidth: 200 }}>
          <div className="modal-label-row"><span className="modal-label">段落数量</span></div>
          <input type="number" min="1" max="20" value={paras} onChange={e => setParas(Math.min(20, +e.target.value))} className="modal-text-input" />
        </div>
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>生成</button></div>
      <OutputBlock label="Lorem Ipsum" value={output} />
    </div>
  );
};

const MarkdownModal = () => {
  const [input, setInput] = useState(`# Hello World\n\n**粗体文字** 和 *斜体文字*\n\n- 列表项 1\n- 列表项 2\n\n> 引用文字\n\n\`代码\``);
  const preview = useMemo(() => txt.markdownToHtmlPreview(input), [input]);
  return (
    <div className="modal-body" style={{ overflow: 'hidden' }}>
      <div className="modal-inputs" style={{ flex: 1, overflow: 'hidden' }}>
        <div className="modal-input-block" style={{ overflow: 'hidden' }}>
          <div className="modal-label-row"><span className="modal-label">Markdown 输入</span></div>
          <textarea className="modal-textarea" value={input} onChange={e => setInput(e.target.value)} spellCheck={false} style={{ flex: 1, minHeight: 300 }} />
        </div>
        <div className="modal-input-block" style={{ overflow: 'hidden' }}>
          <div className="modal-label-row"><span className="modal-label">渲染预览</span></div>
          <div className="markdown-preview" dangerouslySetInnerHTML={{ __html: preview }} />
        </div>
      </div>
    </div>
  );
};

const FigletModal = () => {
  const [input, setInput] = useState('Hello');
  const [output, setOutput] = useState('');
  const run = () => { try { setOutput(txt.figlet(input)); } catch (e) { setOutput(e.message); } };
  return (
    <div className="modal-body">
      <Textarea label="输入文字（支持英文和数字）" value={input} onChange={setInput} placeholder="Hello" rows={2} />
      <div className="modal-run-row"><button className="run-btn" onClick={run}>生成 ASCII 艺术字</button></div>
      <OutputBlock label="ASCII 艺术字" value={output} />
    </div>
  );
};

const ExtractModal = () => {
  const [input, setInput] = useState('联系我们：\n邮箱 user@example.com 或 admin@test.org\n网址 https://www.example.com\nIP: 192.168.1.1\n电话: 13812345678');
  const [type, setType] = useState('emails');
  const [output, setOutput] = useState('');
  const types = [['emails','邮件地址'],['urls','URL 链接'],['ips','IP 地址'],['phones','电话号码'],['numbers','数字'],['dates','日期'],['hashtags','话题标签'],['mentions','@提及']];
  const run = () => { try { setOutput(txt.extractText(input, type)); } catch (e) { setOutput(e.message); } };
  return (
    <div className="modal-body">
      <Textarea label="输入文本" value={input} onChange={setInput} rows={6} />
      <div className="case-modes">
        {types.map(([v, l]) => (
          <button key={v} className={`case-btn ${type === v ? 'active' : ''}`} onClick={() => setType(v)}>{l}</button>
        ))}
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>提取</button></div>
      <OutputBlock label="提取结果" value={output} />
    </div>
  );
};

const FormulaModal = () => {
  const [input, setInput] = useState('sin(PI/6) * 2 + sqrt(9)');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    try {
      const r = code.evalMathFormula(input);
      setOutput(`= ${r.formatted}`);
    } catch (e) { setError(e.message); }
  };
  const examples = ['1 + 2 * 3', 'sqrt(16)', 'sin(PI/6)', 'log10(1000)', '2 ** 10', '(1 + 5) / 2'];
  return (
    <div className="modal-body">
      <Textarea label="数学表达式" value={input} onChange={setInput} placeholder="sin(PI/6) * 2 + sqrt(9)" rows={3} />
      <div className="cron-examples">
        <span className="modal-label" style={{ marginRight: 8 }}>示例：</span>
        {examples.map(e => <button key={e} className="cron-ex-btn" onClick={() => setInput(e)}>{e}</button>)}
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>计算</button></div>
      <OutputBlock label="结果" value={output} error={error} />
      <div className="modal-hint-center">支持：sin/cos/tan/sqrt/log/log10/PI/E/^/**</div>
    </div>
  );
};

const AsciiTableModal = () => {
  const [from, setFrom] = useState(32);
  const [to, setTo] = useState(127);
  const [output, setOutput] = useState('');
  const run = () => setOutput(txt.asciiTable(from, to));
  return (
    <div className="modal-body">
      <div className="modal-inputs">
        <div className="modal-input-block" style={{ maxWidth: 150 }}>
          <div className="modal-label-row"><span className="modal-label">起始（十进制）</span></div>
          <input type="number" min="0" max="127" value={from} onChange={e => setFrom(+e.target.value)} className="modal-text-input" />
        </div>
        <div className="modal-input-block" style={{ maxWidth: 150 }}>
          <div className="modal-label-row"><span className="modal-label">结束（十进制）</span></div>
          <input type="number" min="0" max="127" value={to} onChange={e => setTo(+e.target.value)} className="modal-text-input" />
        </div>
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>显示</button></div>
      <OutputBlock label="ASCII 表" value={output} />
    </div>
  );
};

const RandomStrModal = () => {
  const [length, setLength] = useState(16);
  const [charset, setCharset] = useState('alphanumeric');
  const [count, setCount] = useState(5);
  const [output, setOutput] = useState('');
  const charsets = [['alphanumeric','字母+数字'],['alpha','仅字母'],['numeric','仅数字'],['hex','十六进制'],['base64','Base64字符'],['symbol','特殊符号']];
  const run = () => setOutput(Array.from({ length: count }, () => txt.randomString(length, charset)).join('\n'));
  return (
    <div className="modal-body">
      <div className="modal-inputs">
        <div className="modal-input-block" style={{ maxWidth: 150 }}>
          <div className="modal-label-row"><span className="modal-label">字符串长度</span></div>
          <input type="number" min="1" max="256" value={length} onChange={e => setLength(+e.target.value)} className="modal-text-input" />
        </div>
        <div className="modal-input-block" style={{ maxWidth: 150 }}>
          <div className="modal-label-row"><span className="modal-label">生成数量</span></div>
          <input type="number" min="1" max="50" value={count} onChange={e => setCount(+e.target.value)} className="modal-text-input" />
        </div>
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">字符集</span></div>
          <select className="modal-select" value={charset} onChange={e => setCharset(e.target.value)}>
            {charsets.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>生成</button></div>
      <OutputBlock label="结果" value={output} />
    </div>
  );
};

// ─── Network Modals ───────────────────────────────────────────────────────────

const IpFormatModal = () => {
  const [input, setInput] = useState('192.168.1.1');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    const r = net.formatIp(input);
    if (r.error) { setError(r.error); setResult(null); }
    else setResult(r);
  };
  return (
    <div className="modal-body">
      <Textarea label="IPv4 地址" value={input} onChange={setInput} placeholder="192.168.1.1" rows={2} />
      <div className="modal-run-row"><button className="run-btn" onClick={run}>格式化</button></div>
      {error && <div className="modal-error">{error}</div>}
      {result && (
        <div className="dt-result-grid">
          {Object.entries(result).map(([k, v]) => (
            <div key={k} className="dt-now-row"><span className="dt-now-key">{k}</span><span className="dt-now-val">{String(v)}</span></div>
          ))}
        </div>
      )}
    </div>
  );
};

const HttpStatusModal = () => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return Object.entries(net.HTTP_STATUS_CODES).filter(([code, info]) =>
      !q || String(code).includes(q) || info.text.toLowerCase().includes(q) || info.desc.toLowerCase().includes(q)
    );
  }, [search]);
  const groupColor = (code) => {
    const n = +code;
    if (n < 200) return '#6366f1'; if (n < 300) return '#10b981';
    if (n < 400) return '#f59e0b'; if (n < 500) return '#ef4444'; return '#8b5cf6';
  };
  return (
    <div className="modal-body">
      <input className="search-input" style={{ width: '100%', marginBottom: 12 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="搜索状态码、名称或描述..." />
      <div className="http-status-grid">
        {filtered.map(([code, info]) => (
          <div key={code} className="http-status-card" onClick={() => navigator.clipboard.writeText(code)}>
            <span className="http-code" style={{ color: groupColor(code) }}>{code}</span>
            <span className="http-text">{info.text}</span>
            <span className="http-desc">{info.desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const PortsModal = () => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return net.COMMON_PORTS.filter(p => !q || String(p.port).includes(q) || p.service.toLowerCase().includes(q) || p.desc.toLowerCase().includes(q));
  }, [search]);
  return (
    <div className="modal-body">
      <input className="search-input" style={{ width: '100%', marginBottom: 12 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="搜索端口号、服务名或描述..." />
      <div className="ports-table">
        <div className="ports-header"><span>端口</span><span>协议</span><span>服务</span><span>描述</span></div>
        {filtered.map(p => (
          <div key={p.port} className="ports-row">
            <span className="port-num">{p.port}</span>
            <span className="port-proto">{p.proto}</span>
            <span className="port-service">{p.service}</span>
            <span className="port-desc">{p.desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const UaModal = () => {
  const [input, setInput] = useState(navigator.userAgent);
  const [result, setResult] = useState(null);
  const run = () => setResult(net.parseUserAgent(input));
  return (
    <div className="modal-body">
      <Textarea label="User-Agent 字符串" value={input} onChange={setInput} rows={3} />
      <div className="modal-run-row"><button className="run-btn" onClick={run}>解析</button></div>
      {result && (
        <div className="dt-result-grid">
          {Object.entries(result).filter(([k]) => k !== 'raw').map(([k, v]) => (
            <div key={k} className="dt-now-row"><span className="dt-now-key">{k}</span><span className="dt-now-val">{String(v)}</span></div>
          ))}
        </div>
      )}
    </div>
  );
};

const HostsModal = () => {
  const [input, setInput] = useState('# Hosts 文件\n127.0.0.1    localhost\n0.0.0.0      ads.example.com\n192.168.1.1  router.local');
  const [output, setOutput] = useState('');
  const run = () => {
    const entries = net.parseHosts(input);
    setOutput(net.formatHosts(entries));
  };
  return (
    <div className="modal-body">
      <div className="modal-inputs">
        <Textarea label="输入 Hosts 文件内容" value={input} onChange={setInput} rows={10} />
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>格式化</button></div>
      <OutputBlock label="格式化结果" value={output} />
    </div>
  );
};

const HttpSendModal = () => {
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('https://jsonplaceholder.typicode.com/posts/1');
  const [headers, setHeaders] = useState('Content-Type: application/json');
  const [body, setBody] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true); setError(''); setOutput('');
    try {
      const hdrs = {};
      headers.split('\n').forEach(l => { const [k, ...v] = l.split(':'); if (k.trim()) hdrs[k.trim()] = v.join(':').trim(); });
      const opts = { method, headers: hdrs };
      if (body && method !== 'GET') opts.body = body;
      const start = Date.now();
      const res = await fetch(url, opts);
      const elapsed = Date.now() - start;
      const text = await res.text();
      let formatted = text;
      try { formatted = JSON.stringify(JSON.parse(text), null, 2); } catch {}
      setOutput(`HTTP ${res.status} ${res.statusText} (${elapsed}ms)\n\n${formatted}`);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };
  return (
    <div className="modal-body">
      <div className="http-url-row">
        <select className="modal-select" style={{ width: 120 }} value={method} onChange={e => setMethod(e.target.value)}>
          {['GET','POST','PUT','PATCH','DELETE','HEAD','OPTIONS'].map(m => <option key={m}>{m}</option>)}
        </select>
        <input className="modal-text-input" style={{ flex: 1 }} value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..." />
      </div>
      <Textarea label="请求头（每行一个 Key: Value）" value={headers} onChange={setHeaders} rows={3} />
      {method !== 'GET' && <Textarea label="请求体（JSON）" value={body} onChange={setBody} rows={4} />}
      <div className="modal-run-row"><button className="run-btn" onClick={run} disabled={loading}>{loading ? '请求中...' : '发送请求'}</button></div>
      <OutputBlock label="响应" value={output} error={error} />
    </div>
  );
};

// ─── Code Modals ──────────────────────────────────────────────────────────────

const RegexModal = () => {
  const [pattern, setPattern] = useState('[A-Z][a-z]+');
  const [flags, setFlags] = useState('g');
  const [text, setText] = useState('Hello World, Nice To Meet You!');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    try { setResult(code.testRegex(pattern, flags, text)); }
    catch (e) { setError(e.message); }
  };
  return (
    <div className="modal-body">
      <div className="regex-top">
        <div className="modal-input-block" style={{ flex: 3 }}>
          <div className="modal-label-row"><span className="modal-label">正则表达式</span></div>
          <div className="regex-input-row"><span className="regex-slash">/</span><input className="modal-text-input regex-pattern" value={pattern} onChange={e => setPattern(e.target.value)} placeholder="[A-Z][a-z]+" /><span className="regex-slash">/</span><input className="modal-text-input regex-flags" value={flags} onChange={e => setFlags(e.target.value)} placeholder="gim" /></div>
        </div>
      </div>
      <Textarea label="测试文本" value={text} onChange={setText} rows={4} />
      <div className="modal-run-row"><button className="run-btn" onClick={run}>测试</button></div>
      {error && <div className="modal-error">{error}</div>}
      {result && (
        <>
          <div className="regex-summary">{result.isMatch ? `✅ 匹配成功，共 ${result.count} 个匹配` : '❌ 无匹配'}</div>
          {result.matches.length > 0 && (
            <div className="regex-matches">
              {result.matches.map((m, i) => (
                <div key={i} className="regex-match-item">
                  <span className="regex-match-idx">{i + 1}</span>
                  <code className="regex-match-val">{m.match}</code>
                  <span className="regex-match-pos">位置 {m.index}</span>
                  {m.groups.length > 0 && <span className="regex-match-groups">捕获组: [{m.groups.join(', ')}]</span>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const JwtModal = () => {
  const [input, setInput] = useState(SAMPLE_INPUTS.JWT || '');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    try { setResult(code.parseJwt(input)); }
    catch (e) { setError(e.message); setResult(null); }
  };
  return (
    <div className="modal-body">
      <Textarea label="JWT Token" value={input} onChange={setInput} placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..." rows={4} />
      <div className="modal-run-row"><button className="run-btn" onClick={run}>解析</button></div>
      {error && <div className="modal-error">{error}</div>}
      {result && (
        <>
          {result.expired !== null && <div className={`jwt-status ${result.expired ? 'expired' : 'valid'}`}>{result.expired ? `⚠️ Token 已过期（${result.expiresAt}）` : `✅ Token 有效（过期时间：${result.expiresAt}）`}</div>}
          <div className="jwt-sections">
            <div className="jwt-section">
              <div className="modal-label">Header</div>
              <pre className="modal-output">{JSON.stringify(result.header, null, 2)}</pre>
            </div>
            <div className="jwt-section">
              <div className="modal-label">Payload</div>
              <pre className="modal-output">{JSON.stringify(result.payload, null, 2)}</pre>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const UuidModal = () => {
  const [type, setType] = useState('uuid4');
  const [count, setCount] = useState(5);
  const [result, setResult] = useState([]);
  const generate = () => {
    const arr = [];
    for (let i = 0; i < count; i++) {
      if (type === 'uuid4') arr.push(code.generateUuid(4));
      else if (type === 'uuid1') arr.push(code.generateUuid(1));
      else if (type === 'ulid') arr.push(code.generateUlid());
      else arr.push(code.generateNanoid());
    }
    setResult(arr);
  };
  return (
    <div className="modal-body">
      <div className="modal-inputs">
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">类型</span></div>
          <select className="modal-select" value={type} onChange={e => setType(e.target.value)}>
            <option value="uuid4">UUID v4（随机）</option>
            <option value="uuid1">UUID v1（时间戳）</option>
            <option value="ulid">ULID</option>
            <option value="nanoid">NanoID</option>
          </select>
        </div>
        <div className="modal-input-block" style={{ maxWidth: 150 }}>
          <div className="modal-label-row"><span className="modal-label">生成数量</span></div>
          <input type="number" min="1" max="50" value={count} onChange={e => setCount(+e.target.value)} className="modal-text-input" />
        </div>
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={generate}>生成</button></div>
      {result.length > 0 && (
        <div className="pw-results">
          {result.map((id, i) => (
            <div key={i} className="pw-item">
              <code className="pw-text">{id}</code>
              <button className="copy-btn" onClick={() => navigator.clipboard.writeText(id)}>复制</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const BaseModal = () => {
  const [value, setValue] = useState('255');
  const [fromBase, setFromBase] = useState(10);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    try { setResult(code.baseConvert(value, fromBase, 16)); }
    catch (e) { setError(e.message); setResult(null); }
  };
  return (
    <div className="modal-body">
      <div className="modal-inputs">
        <div className="modal-input-block" style={{ flex: 2 }}>
          <div className="modal-label-row"><span className="modal-label">输入数值</span></div>
          <input className="modal-text-input" value={value} onChange={e => setValue(e.target.value)} placeholder="255" />
        </div>
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">源进制</span></div>
          <select className="modal-select" value={fromBase} onChange={e => setFromBase(+e.target.value)}>
            <option value="2">二进制 (2)</option>
            <option value="8">八进制 (8)</option>
            <option value="10">十进制 (10)</option>
            <option value="16">十六进制 (16)</option>
          </select>
        </div>
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>转换</button></div>
      {error && <div className="modal-error">{error}</div>}
      {result && (
        <div className="dt-result-grid">
          {[['二进制 (2)', result.binary], ['八进制 (8)', result.octal], ['十进制 (10)', result.decimal], ['十六进制 (16)', '0x' + result.hex], ['Base32', result.base32], ['Base36', result.base36]].map(([k, v]) => (
            <div key={k} className="dt-now-row" style={{ cursor: 'pointer' }} onClick={() => navigator.clipboard.writeText(v)}>
              <span className="dt-now-key">{k}</span>
              <span className="dt-now-val">{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ChmodModal = () => {
  const [octal, setOctal] = useState('755');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    try { setResult(code.chmodCalc(octal)); }
    catch (e) { setError(e.message); setResult(null); }
  };
  return (
    <div className="modal-body">
      <Textarea label="权限值（八进制，如 755 或 644）" value={octal} onChange={setOctal} placeholder="755" rows={2} />
      <div className="modal-run-row"><button className="run-btn" onClick={run}>计算</button></div>
      {error && <div className="modal-error">{error}</div>}
      {result && (
        <div className="chmod-result">
          <div className="chmod-symbolic">{result.symbolic}</div>
          <div className="chmod-grid">
            {[['所有者', result.owner], ['组', result.group], ['其他', result.other]].map(([name, perm]) => (
              <div key={name} className="chmod-item">
                <div className="chmod-name">{name}</div>
                {[['读', perm.read], ['写', perm.write], ['执行', perm.execute]].map(([p, v]) => (
                  <div key={p} className={`chmod-perm ${v ? 'on' : 'off'}`}>{p}: {v ? '✓' : '✗'}</div>
                ))}
              </div>
            ))}
          </div>
          <div className="dt-result-grid">
            <div className="dt-now-row"><span className="dt-now-key">八进制</span><span className="dt-now-val">{result.octal}</span></div>
            <div className="dt-now-row"><span className="dt-now-key">符号表示</span><span className="dt-now-val">{result.symbolic}</span></div>
            <div className="dt-now-row"><span className="dt-now-key">十进制</span><span className="dt-now-val">{result.decimal}</span></div>
          </div>
        </div>
      )}
    </div>
  );
};

const BitwiseModal = () => {
  const [a, setA] = useState('60');
  const [b, setB] = useState('13');
  const [op, setOp] = useState('AND');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const ops = ['AND', 'OR', 'XOR', 'NOT', 'LSHIFT', 'RSHIFT', 'URSHIFT'];
  const run = () => {
    setError('');
    try { setResult(code.bitwiseCalc(a, b, op)); }
    catch (e) { setError(e.message); setResult(null); }
  };
  return (
    <div className="modal-body">
      <div className="modal-inputs">
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">操作数 A</span></div>
          <input className="modal-text-input" value={a} onChange={e => setA(e.target.value)} placeholder="60" />
        </div>
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">操作符</span></div>
          <select className="modal-select" value={op} onChange={e => setOp(e.target.value)}>
            {ops.map(o => <option key={o}>{o}</option>)}
          </select>
        </div>
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">操作数 B</span></div>
          <input className="modal-text-input" value={b} onChange={e => setB(e.target.value)} placeholder="13" disabled={op === 'NOT'} />
        </div>
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>计算</button></div>
      {error && <div className="modal-error">{error}</div>}
      {result && (
        <div className="dt-result-grid">
          {[['A (十进制)', result.a], ['B (十进制)', result.b], ['操作', result.op], ['结果 (十进制)', result.result], ['结果 (十六进制)', result.result_hex], ['A (二进制)', result.a_bin], ['B (二进制)', result.b_bin], ['结果 (二进制)', result.result_bin]].map(([k, v]) => (
            <div key={k} className="dt-now-row"><span className="dt-now-key">{k}</span><span className="dt-now-val" style={{ fontFamily: 'monospace' }}>{String(v)}</span></div>
          ))}
        </div>
      )}
    </div>
  );
};

const UnitModal = () => {
  const [value, setValue] = useState('1');
  const [from, setFrom] = useState('km');
  const [to, setTo] = useState('mile');
  const [category, setCategory] = useState('length');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const units = {
    length: ['m','km','cm','mm','mile','yard','foot','inch','nm'],
    weight: ['kg','g','mg','t','lb','oz','stone'],
    temperature: ['C','F','K'],
    time: ['s','ms','us','min','h','day','week'],
    data: ['B','KB','MB','GB','TB','bit','Kbit','Mbit','Gbit'],
    area: ['m²','km²','cm²','mm²','acre','ha','ft²','in²'],
    speed: ['m/s','km/h','mph','knot'],
  };
  const run = () => {
    setError('');
    try {
      const r = code.convertUnit(value, from, to, category);
      setResult(`${value} ${from} = ${typeof r === 'number' ? r.toFixed(6).replace(/\.?0+$/, '') : r} ${to}`);
    } catch (e) { setError(e.message); }
  };
  return (
    <div className="modal-body">
      <div className="modal-inputs" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">类别</span></div>
          <select className="modal-select" value={category} onChange={e => { setCategory(e.target.value); setFrom(units[e.target.value][0]); setTo(units[e.target.value][1]); }}>
            {Object.keys(units).map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">数值</span></div>
          <input className="modal-text-input" value={value} onChange={e => setValue(e.target.value)} placeholder="1" />
        </div>
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">从</span></div>
          <select className="modal-select" value={from} onChange={e => setFrom(e.target.value)}>
            {(units[category] || []).map(u => <option key={u}>{u}</option>)}
          </select>
        </div>
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">至</span></div>
          <select className="modal-select" value={to} onChange={e => setTo(e.target.value)}>
            {(units[category] || []).map(u => <option key={u}>{u}</option>)}
          </select>
        </div>
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>转换</button></div>
      <OutputBlock label="结果" value={result} error={error} />
    </div>
  );
};

const CurlModal = () => {
  const [curl, setCurl] = useState("curl -X POST 'https://api.example.com/users' -H 'Content-Type: application/json' -d '{\"name\":\"张三\"}'");
  const [lang, setLang] = useState('javascript');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const run = () => {
    setError('');
    try { setOutput(code.curlToCode(curl, lang)); }
    catch (e) { setError(e.message); }
  };
  return (
    <div className="modal-body">
      <Textarea label="curl 命令" value={curl} onChange={setCurl} rows={4} />
      <div className="modal-inputs" style={{ marginTop: 8 }}>
        <div className="modal-input-block">
          <div className="modal-label-row"><span className="modal-label">目标语言</span></div>
          <select className="modal-select" value={lang} onChange={e => setLang(e.target.value)}>
            <option value="javascript">JavaScript (fetch)</option>
            <option value="python">Python (requests)</option>
          </select>
        </div>
      </div>
      <div className="modal-run-row"><button className="run-btn" onClick={run}>转换</button></div>
      <OutputBlock label="代码" value={output} error={error} />
    </div>
  );
};

const HtmlSymbolModal = () => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return code.HTML_SYMBOLS.filter(s => !q || s.name.toLowerCase().includes(q) || s.entity.includes(q) || s.char.includes(q));
  }, [search]);
  return (
    <div className="modal-body">
      <input className="search-input" style={{ width: '100%', marginBottom: 12 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="搜索符号名称或实体..." />
      <div className="html-sym-grid">
        {filtered.map(s => (
          <div key={s.entity} className="html-sym-card" onClick={() => navigator.clipboard.writeText(s.entity)} title={`点击复制 ${s.entity}`}>
            <span className="html-sym-char">{s.char}</span>
            <span className="html-sym-entity">{s.entity}</span>
            <span className="html-sym-name">{s.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const CryptoHashModal = () => {
  const [input, setInput] = useState('Hello, World!');
  const [results, setResults] = useState({});
  const [error, setError] = useState('');
  const run = async () => {
    setError('');
    const enc = new TextEncoder();
    const data = enc.encode(input);
    const out = {};
    for (const algo of ['SHA-1', 'SHA-256', 'SHA-384', 'SHA-512']) {
      try {
        const hash = await crypto.subtle.digest(algo, data);
        const hex = Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
        out[algo] = hex;
      } catch (e) { out[algo] = '不支持: ' + e.message; }
    }
    // MD5 is not in WebCrypto, show note
    out['MD5'] = '浏览器 WebCrypto 不支持 MD5，请使用后端或第三方库';
    setResults(out);
  };
  return (
    <div className="modal-body">
      <Textarea label="输入文本" value={input} onChange={setInput} rows={3} />
      <div className="modal-run-row"><button className="run-btn" onClick={run}>计算哈希</button></div>
      {error && <div className="modal-error">{error}</div>}
      {Object.keys(results).length > 0 && (
        <div className="dt-result-grid">
          {Object.entries(results).map(([algo, hash]) => (
            <div key={algo} className="dt-now-row" style={{ cursor: 'pointer' }} onClick={() => navigator.clipboard.writeText(hash)}>
              <span className="dt-now-key" style={{ minWidth: 80 }}>{algo}</span>
              <span className="dt-now-val" style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{hash}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MyIpModal = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fetch_ = async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch('https://ipapi.co/json/');
      const d = await r.json();
      setInfo(d);
    } catch (e) { setError('获取失败: ' + e.message); }
    setLoading(false);
  };
  return (
    <div className="modal-body">
      <div className="modal-run-row"><button className="run-btn" onClick={fetch_} disabled={loading}>{loading ? '查询中...' : '获取我的 IP 信息'}</button></div>
      {error && <div className="modal-error">{error}</div>}
      {info && (
        <div className="dt-result-grid">
          {[['IP', info.ip],['城市', info.city],['地区', info.region],['国家', info.country_name],['时区', info.timezone],['纬度', info.latitude],['经度', info.longitude],['ISP', info.org]].map(([k, v]) => (
            <div key={k} className="dt-now-row"><span className="dt-now-key">{k}</span><span className="dt-now-val">{String(v ?? '-')}</span></div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── HTML Render Modal ────────────────────────────────────────────────────────

const DEFAULT_HTML_RENDER = `<h1>Hello, World!</h1>
<p>这是一个 <strong>HTML 渲染</strong> 示例。</p>
<ul>
  <li>在左侧输入 HTML</li>
  <li>右侧实时预览效果</li>
</ul>`;

const HtmlRenderModal = () => {
  const [html, setHtml] = useState(DEFAULT_HTML_RENDER);
  return (
    <div className="modal-body htmlrender-body">
      <div className="htmlrender-panes">
        <div className="htmlrender-input-pane">
          <div className="modal-label-row"><span className="modal-label">HTML 输入</span><span className="modal-hint">{html.length} 字符</span></div>
          <textarea
            className="modal-textarea htmlrender-textarea"
            value={html}
            onChange={e => setHtml(e.target.value)}
            placeholder="在此输入 HTML 代码..."
            spellCheck={false}
          />
          <div className="modal-run-row" style={{justifyContent:'flex-start',gap:'8px',paddingTop:'4px'}}>
            <button className="run-btn" style={{padding:'6px 18px',fontSize:'13px'}} onClick={() => setHtml('')}>清空</button>
            <button className="run-btn" style={{padding:'6px 18px',fontSize:'13px',background:'#64748b'}} onClick={() => setHtml(DEFAULT_HTML_RENDER)}>重置示例</button>
          </div>
        </div>
        <div className="htmlrender-preview-pane">
          <div className="modal-label-row"><span className="modal-label">渲染预览</span></div>
          <div className="htmlrender-preview" dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      </div>
    </div>
  );
};

// ─── Notepad Modal ─────────────────────────────────────────────────────────────

const NotepadModal = () => {
  const [text, setText] = useState('');
  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);

  const updateLineNumbers = useCallback(() => {
    if (!lineNumbersRef.current || !textareaRef.current) return;
    const lines = text.split('\n').length;
    const lineNumbers = Array.from({ length: lines }, (_, i) => i + 1).join('\n');
    lineNumbersRef.current.textContent = lineNumbers;
  }, [text]);

  useEffect(() => {
    updateLineNumbers();
  }, [updateLineNumbers]);

  const handleScroll = useCallback(() => {
    if (lineNumbersRef.current && textareaRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      const newText = text.substring(0, start) + '  ' + text.substring(end);
      setText(newText);
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = start + 2;
      }, 0);
    }
  }, [text]);

  const handleClear = useCallback(() => {
    setText('');
  }, []);

  return (
    <div className="modal-body notepad-body">
      <div className="modal-label-row" style={{justifyContent:'flex-end',paddingBottom:'4px'}}>
        <button className="clear-btn" onClick={handleClear}>清空</button>
      </div>
      <div className="notepad-container">
        <div className="notepad-line-numbers" ref={lineNumbersRef}>1</div>
        <textarea
          ref={textareaRef}
          className="notepad-textarea"
          value={text}
          onChange={e => setText(e.target.value)}
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
          placeholder="在此输入文本..."
          spellCheck={false}
        />
      </div>
      <div className="notepad-status">
        <span>{text.split('\n').length} 行</span>
        <span>{text.length} 字符</span>
      </div>
    </div>
  );
};

// ─── Modal Router ─────────────────────────────────────────────────────────────

const ToolModalBody = ({ tool }) => {
  if (tool.type === 'timestamp')  return <TimestampModal />;
  if (tool.type === 'timezone')   return <TimezoneModal />;
  if (tool.type === 'timediff')   return <TimeDiffModal />;
  if (tool.type === 'stopwatch')  return <StopwatchModal />;
  if (tool.type === 'countdown')  return <CountdownModal />;
  if (tool.type === 'cron')       return <CronModal />;
  if (tool.type === 'randomdate') return <RandomDateModal />;
  if (tool.type === 'datetime')   return <DatetimeNowModal />;
  if (tool.type === 's2hms')      return <S2HmsModal />;
  if (tool.type === 'hms2s')      return <Hms2SModal />;
  if (tool.type === 'utcts')      return <TimestampModal />;
  if (tool.type === 'color')      return <ColorModal />;
  if (tool.type === 'contrast')   return <ContrastModal />;
  if (tool.type === 'palette')    return <PaletteModal />;
  if (tool.type === 'colorpick')  return <ColorPickerModal />;
  if (tool.type === 'imgcolor')   return <div className="modal-body"><div className="coming-soon"><div className="coming-soon-icon">🎨</div><div>图片颜色提取器需要文件上传功能，将在后续版本实现</div></div></div>;
  if (tool.type === 'regex')      return <RegexModal />;
  if (tool.type === 'jwt')        return <JwtModal />;
  if (tool.type === 'uuid')       return <UuidModal />;
  if (tool.type === 'base')       return <BaseModal />;
  if (tool.type === 'chmod')      return <ChmodModal />;
  if (tool.type === 'bitwise')    return <BitwiseModal />;
  if (tool.type === 'unit')       return <UnitModal />;
  if (tool.type === 'curl')       return <CurlModal />;
  if (tool.type === 'htmlrender')  return <HtmlRenderModal />;
  if (tool.type === 'notepad')    return <NotepadModal />;
  if (tool.type === 'htmlsym')    return <HtmlSymbolModal />;
  if (tool.type === 'formula')    return <FormulaModal />;
  if (tool.type === 'ipformat')   return <IpFormatModal />;
  if (tool.type === 'httpstatus') return <HttpStatusModal />;
  if (tool.type === 'ports')      return <PortsModal />;
  if (tool.type === 'ua')         return <UaModal />;
  if (tool.type === 'hosts')      return <HostsModal />;
  if (tool.type === 'httpsend')   return <HttpSendModal />;
  if (tool.type === 'myip')       return <MyIpModal />;
  if (tool.type === 'case')       return <CaseModal />;
  if (tool.type === 'sort')       return <SortModal />;
  if (tool.type === 'dedupe')     return <DedupeModal />;
  if (tool.type === 'diff')       return <DiffModal />;
  if (tool.type === 'analysis')   return <AnalysisModal />;
  if (tool.type === 'password')   return <PasswordModal />;
  if (tool.type === 'lorem')      return <LoremModal />;
  if (tool.type === 'markdown')   return <MarkdownModal />;
  if (tool.type === 'figlet')     return <FigletModal />;
  if (tool.type === 'extract')    return <ExtractModal />;
  if (tool.type === 'ascii')      return <AsciiTableModal />;
  if (tool.type === 'randomstr')  return <RandomStrModal />;
  if (tool.type === 'qr')         return <QrModal tool={tool} />;
  if (tool.type === 'crypto_hash')return <CryptoHashModal />;
  if (tool.type === 'crypto_rsa_gen') return <UuidModal />; // placeholder
  // All convert/dual/path/generate/async types
  return <ConvertModal tool={tool} />;
};

// ─── Tool Modal Wrapper ───────────────────────────────────────────────────────

const ToolModal = ({ tool, onClose }) => {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const catName = tool.category?.label || '';
  const modalBoxClass = tool.type === 'htmlrender' ? 'modal-box modal-box-htmlrender' : 'modal-box';

  return (
    <div className="modal-backdrop">
      <div className={modalBoxClass}>
        <div className="modal-header">
          <div className="modal-title-group">
            <div className="modal-title-icon">{tool.icon || '⚙️'}</div>
            <h2 className="modal-title">{tool.label}</h2>
            {catName && <span className="modal-cat-badge">{catName}</span>}
            {tool.desc && <span className="modal-desc">{tool.desc}</span>}
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <ToolModalBody tool={tool} />
      </div>
    </div>
  );
};

// ─── Tool Card ────────────────────────────────────────────────────────────────

const ToolCard = ({ tool, onClick }) => (
  <button className="tool-card" onClick={() => onClick(tool)}>
    <div className="tool-card-label">{tool.label}</div>
    {(tool.inputLang || tool.outputLang) && (
      <div className="tool-card-langs">{[tool.inputLang, tool.outputLang].filter(Boolean).join(' → ')}</div>
    )}
    {tool.desc && <div className="tool-card-desc">{tool.desc}</div>}
  </button>
);

// ─── Main Toolbox ─────────────────────────────────────────────────────────────

const Toolbox = () => {
  const [activeCategory, setActiveCategory] = useState('favorites');
  const [activeTool, setActiveTool] = useState(null);
  const [search, setSearch] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Record visit count on mount — ref guard prevents StrictMode double-fire
  const visitRecorded = useRef(false);
  useEffect(() => {
    if (visitRecorded.current) return;
    visitRecorded.current = true;
    const key = 'toolbox_visit_count';
    const prev = parseInt(localStorage.getItem(key) || '0', 10);
    localStorage.setItem(key, String(prev + 1));
  }, []);

  // Build favorites list from all tools
  const favoriteTools = useMemo(() => {
    const allTools = Object.values(TOOLS).flat();
    return FAVORITES_IDS.map(id => allTools.find(t => t.id === id)).filter(Boolean);
  }, []);

  const searchResults = useMemo(() => {
    if (!search.trim()) return [];
    return globalSearch(search);
  }, [search]);

  const currentTools = useMemo(() => {
    if (activeCategory === 'favorites') return favoriteTools;
    return TOOLS[activeCategory] || [];
  }, [activeCategory, favoriteTools]);

  const handleSearch = useCallback((e) => {
    setSearch(e.target.value);
    setShowSearchResults(!!e.target.value.trim());
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearch('');
    setShowSearchResults(false);
  }, []);

  const handleToolClick = useCallback((tool) => setActiveTool(tool), []);
  const handleCloseModal = useCallback(() => setActiveTool(null), []);
  const handleCategoryChange = useCallback((catId) => { setActiveCategory(catId); handleClearSearch(); }, [handleClearSearch]);

  const currentCatLabel = activeCategory === 'favorites' ? '常用' : (CATEGORIES.find(c => c.id === activeCategory)?.label ?? '');
  const isComingSoon = currentTools.length === 0 && activeCategory !== 'favorites';
  const displayTools = showSearchResults ? searchResults : currentTools;

  return (
    <div className="toolbox-page">
      {/* Global Top Bar */}
      <div className="toolbox-global-bar">
        <div className="global-bar-brand">
          <span className="global-bar-title">M云开发者工具箱</span>
        </div>
        <div className="global-search-wrap">
          <span className="global-search-icon">🔍</span>
          <input
            className="global-search-input"
            value={search}
            onChange={handleSearch}
            placeholder="全局搜索工具（如：JSON格式化、时间戳、Base64...）"
          />
          {search && <button className="global-search-clear" onClick={handleClearSearch}>×</button>}
        </div>
        <div className="global-bar-right">
          {showSearchResults && <span className="search-result-count">{searchResults.length} 个结果</span>}
        </div>
      </div>

      <div className="toolbox-body">
        {/* Left Sidebar */}
        <aside className="toolbox-sidebar">
          {/* Categories section */}
          <div className="sidebar-section-header sidebar-section-header-first">Categories</div>
          <nav className="sidebar-nav">
            {/* 常用 (Favorites) as first item */}
            <button
              className={`sidebar-item ${!showSearchResults && activeCategory === 'favorites' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('favorites')}
            >
              <span className="sidebar-item-label">常用</span>
              <span className="sidebar-item-count">{favoriteTools.length}</span>
            </button>
            
            {/* Other categories */}
            {CATEGORIES.filter(cat => cat.id !== 'network' && cat.id !== 'image').map((cat, index) => (
              <button
                key={cat.id}
                className={`sidebar-item ${index === 0 ? 'sidebar-item-first-category' : ''} ${!showSearchResults && activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => handleCategoryChange(cat.id)}
              >
                <span className="sidebar-item-label">{cat.label}</span>
                {(TOOLS[cat.id] || []).length > 0 && (
                  <span className="sidebar-item-count">{(TOOLS[cat.id] || []).length}</span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="toolbox-main">
          {/* Section header */}
          <div className="toolbox-topbar">
            <div className="topbar-title-group">
              <h2 className="topbar-title">
                {showSearchResults ? `搜索结果：「${search}」` : currentCatLabel}
              </h2>
              <span className="topbar-count">{displayTools.length} 个工具</span>
            </div>
          </div>

          {/* Tool Grid */}
          {!showSearchResults && isComingSoon ? (
            <div className="coming-soon">
              <div className="coming-soon-icon">🚧</div>
              <div className="coming-soon-text">即将推出</div>
              <div className="coming-soon-sub">{currentCatLabel} 类工具正在开发中...</div>
            </div>
          ) : displayTools.length === 0 ? (
            <div className="coming-soon">
              <div className="coming-soon-icon">🔎</div>
              <div className="coming-soon-text">未找到匹配工具</div>
              <div className="coming-soon-sub">试试其他关键词</div>
            </div>
          ) : (
            <div className="tool-grid">
              {displayTools.map(tool => (
                <ToolCard key={tool.id} tool={tool} onClick={handleToolClick} />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Tool Modal */}
      {activeTool && <ToolModal tool={activeTool} onClose={handleCloseModal} />}
    </div>
  );
};

export default Toolbox;
